import type { CSSProperties } from "react";

import Sentry from "@similarweb/sentry-logger";

import { CookieManager } from "app/components/cookie-manager/CookieManager";
import { TEAMS } from "app/constants/teamName";

export enum EHooksVersions {
    V4 = "v4",
    V5 = "v5",
    V6 = "v6",
}
interface IHooksProModalCustomStyles {
    overlay?: CSSProperties;
    content?: CSSProperties;
}

const cookieManager = new CookieManager();

interface HookApi {
    open: (param: {
        key: string;
        trackId?: string;
        version?: string;
        location?: string;
        currentPageLocation?: string;
        defaultStep?: string;
    }) => void;
    close: () => void;
    style: (arg: IHooksProModalCustomStyles) => void;
}
interface SwHook {
    track(category: string, action: string, name: string): void;
    getFrame(bundle: string, id?: string, remoteCaller?: Window): { api: HookApi } | any;
    createFrame(
        bundle: string,
        id?: string,
        zorder?: number,
        sizer?: string,
        onSetupCallback?: (bundle: string, id: string) => any,
        creator?: Window,
        context?: {},
    ): { api: HookApi } | any;
    destroyFrame(bundle: string, id?: string, destroyer?: Window): any;
}
declare global {
    interface Window {
        SwHook: SwHook;
        qualified;
    }
}

enum QualifiedExperienceIDs {
    "zh-cn" = 1704744756854,
    "fr-fr" = 1704745155010,
    "de-de" = 1704745328169,
    "ja-jp" = 1704745545372,
    "es-es" = 1704745621254,
    "it-it" = 1704745440722,
    "en-us" = 1704743657629,
}

(async () => {
    if (window) {
        window.addEventListener("onHookClose", (data) => {
            if (typeof window.qualified === "function" && data?.detail?.primary === "Chat") {
                const experienceID = getExperienceID();
                window.qualified("showExperience", `experience-${experienceID}`);
            }
        });
    }
    return window.removeEventListener("onHookClose", () => {});
})();

const getExperienceID = (): number => {
    const local = cookieManager.getCookie("locale") || "en-us";
    return QualifiedExperienceIDs[local];
};

const waitForSwHook = async (maxRetries = 5, delay = 300): Promise<boolean> => {
    let attempts = 0;
    while (attempts < maxRetries) {
        if (window.SwHook?.getFrame) {
            return true;
        }
        await new Promise((resolve) => setTimeout(resolve, delay));
        attempts++;
    }
    return false;
};

export async function openHook(
    key: string,
    overrideTrackingKey?: string,
    location?: string,
    version?: string,
    defaultStep?: string,
): Promise<void> {
    const isReady = await waitForSwHook();
    if (!isReady) {
        const errorMessage = "SwHook is not available after waiting.";
        Sentry.error(errorMessage, new Error(errorMessage), {
            tags: { openHookKey: key, openHookLocation: location, openHookVersion: version, team: TEAMS.LYNX },
        });
        return;
    }

    const frame = (await window.SwHook.getFrame("hook")) as { api: HookApi };
    frame.api.open({
        key,
        trackId: overrideTrackingKey,
        location,
        currentPageLocation: window.location.href,
        version,
        defaultStep,
    });
}
